import { useMemo } from "react";
import type { Period } from "@/pages/admin/reports/page";

interface Product {
  marketplace: string;
  [key: string]: unknown;
}

interface MarketplaceChartProps {
  products: Product[];
  period: Period;
}

const MARKETPLACE_COLORS: Record<string, { bar: string; bg: string; text: string }> = {
  Amazon: { bar: "#f97316", bg: "bg-orange-100", text: "text-orange-700" },
  "Mercado Livre": { bar: "#eab308", bg: "bg-yellow-100", text: "text-yellow-700" },
  Shopee: { bar: "#ea580c", bg: "bg-orange-200", text: "text-orange-800" },
  "Magazine Luiza": { bar: "#3b82f6", bg: "bg-blue-100", text: "text-blue-700" },
  Americanas: { bar: "#ef4444", bg: "bg-red-100", text: "text-red-700" },
  Kabum: { bar: "#a855f7", bg: "bg-purple-100", text: "text-purple-700" },
};

const CLICK_MULTIPLIERS: Record<Period, Record<string, number>> = {
  "7d": { Amazon: 1.0, "Mercado Livre": 0.82, Shopee: 0.65, "Magazine Luiza": 0.48, Americanas: 0.35, Kabum: 0.28 },
  "30d": { Amazon: 1.0, "Mercado Livre": 0.78, Shopee: 0.61, "Magazine Luiza": 0.44, Americanas: 0.32, Kabum: 0.25 },
  "90d": { Amazon: 1.0, "Mercado Livre": 0.75, Shopee: 0.58, "Magazine Luiza": 0.42, Americanas: 0.30, Kabum: 0.22 },
};

const BASE_CLICKS: Record<Period, number> = {
  "7d": 4200,
  "30d": 16800,
  "90d": 48000,
};

export default function MarketplaceChart({ products, period }: MarketplaceChartProps) {
  const data = useMemo(() => {
    const counts = products.reduce<Record<string, number>>((acc, p) => {
      acc[p.marketplace] = (acc[p.marketplace] || 0) + 1;
      return acc;
    }, {});

    const base = BASE_CLICKS[period];
    const multipliers = CLICK_MULTIPLIERS[period];

    return Object.entries(counts)
      .map(([mp, count]) => ({
        name: mp,
        products: count,
        clicks: Math.round(base * (multipliers[mp] || 0.2) * (count / products.length + 0.5)),
      }))
      .sort((a, b) => b.clicks - a.clicks);
  }, [products, period]);

  const maxClicks = Math.max(...data.map((d) => d.clicks));
  const totalClicks = data.reduce((s, d) => s + d.clicks, 0);

  return (
    <div className="space-y-4">
      {data.map((item) => {
        const pct = Math.round((item.clicks / totalClicks) * 100);
        const barW = Math.round((item.clicks / maxClicks) * 100);
        const c = MARKETPLACE_COLORS[item.name] || { bar: "#6b7280", bg: "bg-gray-100", text: "text-gray-700" };

        return (
          <div key={item.name}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>
                {item.name}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{item.clicks.toLocaleString("pt-BR")}</span>
                <span className="text-xs font-bold text-gray-700">{pct}%</span>
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${barW}%`, backgroundColor: c.bar }}
              />
            </div>
          </div>
        );
      })}

      <div className="pt-3 border-t border-gray-100 mt-2">
        <p className="text-xs text-gray-400 text-center">
          Total: <span className="font-bold text-gray-700">{totalClicks.toLocaleString("pt-BR")}</span> cliques
        </p>
      </div>
    </div>
  );
}
